import ApiClient from "ApiClient";

export type VoiceOption = {
  id: string;
  label: string;
  agentId?: string;
  isDefault?: boolean;
};

const CHARACTER_NAMES: Record<string, string> = {
  "0fbdXLXuDBZXm2IHek4L": "Veda",
};

const FIRST_VOICE = { id: "0fbdXLXuDBZXm2IHek4L", label: "Veda" };
export const DEFAULT_VOICE_ID = FIRST_VOICE.id;
export function voiceDisplayName(id?: string): string {
  if (!id) return "—";
  return CHARACTER_NAMES[id] || id;
}

function characterName(id: string, label?: string): string {
  if (CHARACTER_NAMES[id]) return CHARACTER_NAMES[id];
  const text = String(label || "").trim();
  if (!text || /^voice\s*\d*$/i.test(text)) return "Agent";
  return text;
}

/**
 * Put the default voice first, and anything else the agent reports after it.
 *
 * The app offers one voice, so this is normally a list of one and the picker
 * does not appear at all. It still tolerates extra voices rather than dropping
 * them, so a voice added on the agent shows up without a code change.
 */
function withVedaCenter(voices: VoiceOption[]): VoiceOption[] {
  const byId = new Map(voices.map((voice) => [voice.id, voice]));
  const ordered: VoiceOption[] = [byId.get(DEFAULT_VOICE_ID) || { ...FIRST_VOICE }];
  for (const voice of voices) {
    if (voice.id === DEFAULT_VOICE_ID) continue;
    ordered.push(voice);
  }
  return ordered;
}

function apiErrorText(body: any, fallback: string): string {
  if (!body) return fallback;
  if (typeof body === "string" && body.trim()) return body;
  return String(body.message || body.Message || body.error || body.title || fallback);
}

export function voiceStartErrorMessage(error: unknown): string {
  if (error == null) return "Could not start the voice assistant. Please try again.";
  if (typeof error === "string") return mapVoiceErrorText(error);
  if (typeof error === "object") {
    const err = error as Record<string, unknown>;
    const name = String(err.name || "");
    const nested = err.message ?? err.Message ?? err.error;
    const text = typeof nested === "string" ? nested : nested != null ? String(nested) : "";
    if (name === "NotAllowedError" || /permission denied|notallowed/i.test(text)) {
      return "Microphone access was denied. Allow the microphone in your browser settings, then try again.";
    }
    if (name === "NotFoundError" || /requested device not found|no microphone/i.test(text)) {
      return "No microphone was found. Connect a microphone and try again.";
    }
    if (name === "NotReadableError" || /could not start audio|in use/i.test(text)) {
      return "The microphone is in use by another application. Close it and try again.";
    }
    if (name === "SecurityError" || /secure context|https:\/\//i.test(text)) {
      return "Microphone needs a secure page. Open the app with https://, not http://.";
    }
    if (name === "TimeoutError" || /timed out|timeout/i.test(text)) {
      return "Could not reach the server. Check your connection and try again.";
    }
    if (name === "AbortError") {
      return /timed out|timeout|signal|aborted/i.test(text)
        ? "Could not reach the server. Check your connection and try again."
        : "Microphone request was cancelled.";
    }
    if (text) return mapVoiceErrorText(text);
  }
  return "Could not start the voice assistant. Please try again.";
}

function mapVoiceErrorText(text: string): string {
  const value = text.trim();
  if (!value) return "Could not start the voice assistant. Please try again.";
  if (/blocked/i.test(value)) {
    return "Voice assistant access is blocked for this account. Contact an administrator.";
  }
  if (/unresponsive|failed to fetch|networkerror|load failed|timed out|timeout/i.test(value)) {
    return "Could not reach the server. Check your connection and try again.";
  }
  if (/signed url failed|denied the signed url|403|does not have permission to get URL|<html/i.test(value)) {
    return "Could not start the voice assistant. ElevenLabs denied access. Enable signed-URL authentication on the agent and use an API key from the same workspace.";
  }
  return value;
}

export async function getVoiceSignedUrl(
    api: ApiClient,
    agentId?: string,
    voiceId?: string
): Promise<{ signedUrl: string; sessionId: number }> {
  const query: Record<string, string> = {};
  if (agentId) query.agentId = agentId;
  if (voiceId) query.voiceId = voiceId;
  const res = await api.get("/api/voice/signed-url", Object.keys(query).length ? query : undefined, { timeout: 12000 });
  if (!res.ok) {
    const fallback = isVoiceAccessBlocked(res.body, res.status)
      ? "Voice assistant access is blocked for this account. Contact an administrator."
      : "Could not start the voice assistant. Please try again.";
    throw new Error(apiErrorText(res.body, fallback));
  }
  const signedUrl = res.body?.signedUrl || res.body?.SignedUrl;
  const sessionId = Number(res.body?.sessionId ?? res.body?.SessionId ?? 0);
  if (!signedUrl) throw new Error("Could not start the voice assistant. Please try again.");
  return { signedUrl, sessionId };
}

export function isVoiceAccessBlocked(body: any, status?: number): boolean {
  if (status === 403) return true;
  const message = String(body?.message || body?.Message || "");
  return /blocked/i.test(message);
}

export async function heartbeatVoiceSession(
  api: ApiClient,
  sessionId: number
): Promise<"ok" | "blocked" | "error"> {
  if (!sessionId) return "ok";
  const res = await api.post(`/api/voice/sessions/${sessionId}/heartbeat`, {});
  if (res.ok) return "ok";
  if (isVoiceAccessBlocked(res.body, res.status)) return "blocked";
  return "error";
}

export async function endVoiceSession(api: ApiClient, sessionId: number, status = "ended"): Promise<void> {
  if (!sessionId) return;
  await api.post(`/api/voice/sessions/${sessionId}/end`, { status });
}

export function endVoiceSessionKeepAlive(sessionId: number, status = "ended"): void {
  if (!sessionId) return;
  const token = localStorage.getItem("accessToken");
  const base = (process.env.REACT_APP_API_URL || "").trim();
  void fetch(`${base}/api/voice/sessions/${sessionId}/end`, {
    method: "POST",
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
    keepalive: true,
  });
}

export async function getVoiceOptions(api: ApiClient): Promise<VoiceOption[]> {
  const res = await api.get("/api/voice/voices");
  if (!res.ok) return [];
  const raw = res.body?.voices || res.body?.Voices || [];
  const mapped = raw
    .map((item: any) => ({
      id: String(item.id || item.Id || ""),
      label: characterName(String(item.id || item.Id || ""), item.label || item.Label),
      agentId: item.agentId || item.AgentId || undefined,
      isDefault: Boolean(item.isDefault || item.IsDefault),
    }))
    .filter((item: VoiceOption) => item.id);
  return withVedaCenter(mapped);
}
